/**
 * Lossless JSON parsing for integers that exceed IEEE-754 double precision.
 *
 * JSON.parse silently rounds any integer above Number.MAX_SAFE_INTEGER
 * (9007199254740991). A 64-bit DBC signal such as 24262061215995474 becomes the
 * double 24262061215995472, which V8 then prints as "24262061215995470" — so the
 * digits are already gone before any UI code runs.
 *
 * parseJsonPreservingBigIntegers quotes such integers in the raw response text
 * before parsing, delivering them as strings with every digit intact. Callers
 * opt in per fetcher; this module changes nothing on its own.
 *
 * The in-memory representation is deliberately a STRING, never a BigInt:
 *   - tsconfig targets es5, so BigInt literals are a TS2737 compile error;
 *   - JSON.stringify throws on a BigInt, which would break dashboard save;
 *   - react-dom 18.2 renders a BigInt JSX child as nothing, silently.
 * Use BigInt only as a transient inside a formatting helper.
 */

const MAX_SAFE_INT_STR = Number.MAX_SAFE_INTEGER.toString(); // "9007199254740991"
const MAX_SAFE_DIGITS = MAX_SAFE_INT_STR.length; // 16

/**
 * Tokenizes JSON strings and numbers so the two can be told apart. Capture
 * groups: 1 = integer part, 2 = fractional part, 3 = exponent.
 *
 * The string alternative comes first and consumes escapes (\\.), so a quoted
 * value containing digits, quotes or backslashes is matched whole and skipped.
 */
const STRINGS_OR_NUMBERS =
  /"(?:\\.|[^"])*"|-?(0|[1-9][0-9]*)(\.[0-9]+)?([eE][+-]?[0-9]+)?/g;

/**
 * Cheap pre-screen: is there any bare integer here that could possibly exceed
 * MAX_SAFE_INTEGER? Roughly 0.4x the cost of JSON.parse itself, versus ~4x for
 * the full pre-pass, so payloads with nothing to fix stay near-free.
 *
 * Anchoring to a JSON structural delimiter (or the very start of the document)
 * is what excludes digits inside quoted strings and digits after a decimal
 * point. Requiring 17 digits — or 16 beginning with 9 — is what excludes the
 * safe 1e15..9.007e15 band: any 16-digit integer above 9007199254740991 must
 * start with a 9.
 *
 * \s* not \s?: JSON permits any amount of whitespace after ':' or ',', and
 * `{"v" :   24262061215995474}` must not slip through.
 *
 * Deliberately NOT global — a /g regex carries lastIndex across .test() calls
 * and would intermittently return false on a repeat scan.
 */
const BIG_INT_GUARD = /(?:^|[:,\[])\s*-?(?:[0-9]{17}|9[0-9]{15})/;

/**
 * Out-of-memory backstop, NOT a performance ceiling.
 *
 * The requirement is that a value the backend sent is never altered on its way
 * to the screen, so this is deliberately set far above any payload the opted-in
 * fetchers actually return — the largest measured is a 25009-row table at ~3.7M
 * chars, which costs ~45ms and stays well clear. The pathological payloads
 * (fleet line charts, ~5M chars on a 5-second refresh) are excluded from the
 * pre-pass by panel type instead, so they never reach this check.
 *
 * It exists only so that a pathological response cannot OOM the tab:
 * String.replace materializes a second copy of the whole document plus a slice
 * per match, roughly a 10-17x transient heap amplification.
 *
 * Above this size digits ARE lost, which is why it warns in dev. If you ever
 * see that warning in practice, raise this rather than accepting the loss.
 *
 * Measured in UTF-16 code units, which equals bytes for the ASCII JSON these
 * endpoints return.
 */
const MAX_PREPASS_CHARS = 32_000_000;

/**
 * The only ClickHouse types that can hold a value above MAX_SAFE_INTEGER.
 * Int32/UInt32 top out at 4.3e9, and Float64 loses its digits at the source, so
 * quoting a float recovers nothing and would break a numeric comparison.
 */
const BIG_INT_COLUMN_TYPES = [
  "Int64",
  "UInt64",
  "Nullable(Int64)",
  "Nullable(UInt64)",
];

/**
 * Parses JSON, delivering integers above MAX_SAFE_INTEGER as exact strings.
 *
 * Floats and e-notation are left as numbers: their digits died in the source
 * Float64, so quoting them recovers no precision, and a quoted float compared
 * against a UInt64 ClickHouse column is a hard 500 rather than an empty result.
 *
 * Numbers inside quoted strings are left alone by design — a pre-pass cannot
 * reach into a JSON-encoded string, so nested JSON.parse call sites stay lossy.
 */
export function parseJsonPreservingBigIntegers(text: string): any {
  // Preserve JSON.parse's own error for empty input.
  if (!text) return JSON.parse(text);

  // Nothing oversized in here — the overwhelmingly common case.
  if (!BIG_INT_GUARD.test(text)) return JSON.parse(text);

  if (text.length > MAX_PREPASS_CHARS) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        `[bb:bigint] payload of ${text.length} chars exceeds the ${MAX_PREPASS_CHARS}-char pre-pass ceiling; integers above ${MAX_SAFE_INT_STR} will lose digits`
      );
    }
    return JSON.parse(text);
  }

  const processed = text.replace(
    STRINGS_OR_NUMBERS,
    (match, digits: string, fractional: string, exponential: string) => {
      // A quoted string — already exact, and its digits are not ours to touch.
      if (match.charCodeAt(0) === 34 /* " */) return match;

      // Integers only. A float or e-notation number has already lost whatever
      // precision it was going to lose.
      if (fractional || exponential) return match;

      if (!digits) return match;

      // Fewer digits than MAX_SAFE_INTEGER: always safe.
      if (digits.length < MAX_SAFE_DIGITS) return match;

      // Same digit count: an equal-length digit-string comparison is exactly a
      // numeric comparison.
      if (digits.length === MAX_SAFE_DIGITS && digits <= MAX_SAFE_INT_STR) {
        return match;
      }

      return '"' + match + '"';
    }
  );

  return JSON.parse(processed);
}

/**
 * Does this column's declared ClickHouse type permit values above
 * MAX_SAFE_INTEGER? Used to decide whether an exact digit-string may be
 * formatted as a number (hex, units, roundoff) rather than printed verbatim.
 *
 * Type-driven rather than value-shape-driven on purpose: a String column
 * holding a 17-digit ICCID must not be hex-formatted.
 */
export function isBigIntColumnType(columnType?: string | null): boolean {
  return !!columnType && BIG_INT_COLUMN_TYPES.indexOf(columnType) !== -1;
}

/**
 * Is this an exact integer string that JSON.parse could not have produced
 * losslessly as a number? True for the values parseJsonPreservingBigIntegers
 * quotes, false for anything a plain Number() round-trips safely.
 *
 * Callers use it to route a value to BigInt-backed formatting, and to decide
 * whether an outbound filter value must skip parseInt/parseFloat.
 */
export function isOversizedIntegerString(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!/^-?[0-9]+$/.test(trimmed)) return false;
  return !Number.isSafeInteger(Number(trimmed));
}
